import * as Highcharts from 'highcharts';
import * as Dashboards from '@highcharts/dashboards';
import DataGrid from '@highcharts/dashboards/es-modules/DataGrid/DataGrid';
import exportModule from 'highcharts/modules/exporting';
import offlineExportingModule from 'highcharts/modules/offline-exporting';
import stockModule from 'highcharts/modules/stock';
import annotationsModule from 'highcharts/modules/annotations';
import drilldownModule from 'highcharts/modules/drilldown';
import xrangeModule from 'highcharts/modules/xrange';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsDashboardPlugin from '@highcharts/dashboards/es-modules/Dashboards/Plugins/HighchartsPlugin';
import DataGridPlugin from '@highcharts/dashboards/es-modules/Dashboards/Plugins/DataGridPlugin';
import { i18n } from '@/plugins/internationalization/i18n';

const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'] as const;

/**
 * Pushes the *current* i18n locale into Highcharts' global `lang`. Highcharts holds `lang` as
 * process-global state resolved at render time, so this is the single place it is set — but it has to
 * be re-run whenever the locale changes, not only at module load. At module load the locale is still
 * the `createI18n` default ('da'), so charts rendered for an English user showed Danish month names
 * ("Maj '26") until this was re-applied. Called again from the `SetUser` mutation, where the user's
 * language becomes known.
 */
export function applyHighchartsLocale(): void {
    const months = MONTH_KEYS.map((key) => i18n.global.t(`layouts.dashboard.charts.months.${key}`) as string);

    Highcharts.setOptions({
        lang: {
            numericSymbols: [null, 'M', 'G', 'T', 'P', 'E'] as any as string[],
            months,
            // Datetime axis and navigator labels use the abbreviated names, which otherwise fall back
            // to Highcharts' English defaults. All supported locales abbreviate as the first three letters.
            shortMonths: months.map((month) => month.slice(0, 3)),
            decimalPoint: i18n.global.locale === 'en' ? '.' : ',',
        },
    });
}

applyHighchartsLocale();

HighchartsMore(Highcharts);
exportModule(Highcharts);
stockModule(Highcharts);
annotationsModule(Highcharts);
drilldownModule(Highcharts);
offlineExportingModule(Highcharts);
xrangeModule(Highcharts);

HighchartsDashboardPlugin.custom.connectHighcharts(Highcharts);
// @ts-ignore
Dashboards.PluginHandler.addPlugin(HighchartsDashboardPlugin);

DataGridPlugin.custom.connectDataGrid(DataGrid);
// @ts-ignore
Dashboards.PluginHandler.addPlugin(DataGridPlugin);

function getWeek(date: Date) {
    date.setUTCHours(0, 0, 0, 0);

    // Thursday in current week decides the year.
    date.setUTCDate(date.getUTCDate() + 3 - ((date.getUTCDay() + 6) % 7));

    // January 4 is always in week 1.
    const week1 = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));

    // Adjust to Thursday in week 1 and count number of weeks from date to week1.
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getUTCDay() + 6) % 7)) / 7);
}

/* eslint-disable no-import-assign */
/* @ts-ignore */
Highcharts.dateFormats = {
    W(timestamp: number) {
        return getWeek(new Date(timestamp));
    },
    Z(timestamp: number) {
        const date = new Date(timestamp);

        const week = getWeek(date);

        const year = date.getUTCFullYear() % 100;

        return `${week}.${year}`;
    },
};
/* eslint-enable no-import-assign */
